// ===================================================================
// SignaturePad.tsx
// Canvas para que el profesional dibuje su firma manuscrita.
// Usado en el wizard de emisión de recetas electrónicas.
// ===================================================================

import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';

import { Eraser, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Cursor propio para el recuadro de la firma.
 *
 * El `crosshair` del sistema se dibuja invirtiendo el fondo, y sobre el blanco
 * del canvas queda invisible en Windows: se firmaba a ciegas. Este es una cruz
 * negra con halo blanco, así se ve tanto sobre claro como sobre el trazo.
 */
const CURSOR_CRUZ =
  "url(\"data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">' +
      '<g stroke="#fff" stroke-width="3" stroke-linecap="round">' +
        '<line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="12" x2="21" y2="12"/>' +
      '</g>' +
      '<g stroke="#111" stroke-width="1.5" stroke-linecap="round">' +
        '<line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="12" x2="21" y2="12"/>' +
      '</g>' +
    '</svg>'
  ) +
  "\") 12 12, crosshair";

interface SignaturePadProps {
  /** Se invoca cada vez que cambia el estado de la firma (vacía/no vacía + dataURL) */
  onChange?: (dataUrl: string | null) => void;
  /** Nombre y matrícula del firmante para mostrar como label arriba del canvas */
  signerLabel?: string;
  /** Mostrar mensaje de error si la firma está vacía y se intentó enviar */
  errorMessage?: string;
  /** Disabled para mostrar firma ya capturada (read-only preview) */
  disabled?: boolean;
}

/**
 * Componente de firma manuscrita.
 * - Canvas responsive (se adapta al ancho del contenedor).
 * - Funciona con mouse, trackpad, touch y stylus.
 * - Devuelve la firma como PNG dataURL al padre vía onChange.
 */
const SignaturePad: React.FC<SignaturePadProps> = ({
  onChange,
  signerLabel,
  errorMessage,
  disabled = false,
}) => {
  const sigRef = useRef<SignatureCanvas | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [canvasWidth, setCanvasWidth] = useState(500);

  // Hacemos el canvas responsive midiendo el ancho del contenedor
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        // Le restamos 4px de borde para que no se desborde
        setCanvasWidth(Math.max(280, w - 4));
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  /**
   * Exporta la firma recortada al trazo.
   *
   * El canvas mide 500x200 y la firma ocupa una fracción: si se exporta
   * entero, el PNG viaja con un montón de blanco alrededor. Del otro lado
   * —el PDF de Qbi2— la imagen se ajusta a su recuadro, así que ese blanco
   * se lleva casi todo el espacio y el trazo queda diminuto.
   *
   * Recortando al contenido, el trazo ocupa todo el PNG y se ve del tamaño
   * que corresponde sin cambiar nada del otro lado.
   */
  const exportarFirma = (): string | null => {
    const canvas = sigRef.current?.getCanvas();
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return sigRef.current?.toDataURL('image/png') ?? null;

    try {
      // Recorte propio en vez de getTrimmedCanvas de la librería: esa devolvía
      // un recorte equivocado —la firma llegaba al PDF como una imagen de
      // 100x200, vertical, cuando una firma es ancha y baja— y encima se comía
      // el error en silencio.
      const { width: w, height: h } = canvas;
      const px = ctx.getImageData(0, 0, w, h).data;
      let x0 = w, y0 = h, x1 = -1, y1 = -1;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (px[(y * w + x) * 4 + 3] > 10) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
        }
      }
      if (x1 < 0) return null;

      const margen = 8;
      x0 = Math.max(0, x0 - margen); y0 = Math.max(0, y0 - margen);
      x1 = Math.min(w - 1, x1 + margen); y1 = Math.min(h - 1, y1 + margen);
      const cw = x1 - x0 + 1;
      const ch = y1 - y0 + 1;

      // Se agranda a un ancho fijo. Si del otro lado la imagen se coloca a su
      // tamaño natural, una firma de 300 px de ancho se ve diminuta y una de
      // 900 se ve como corresponde. Nunca se achica.
      const escala = Math.max(1, 900 / cw);
      const salida = document.createElement('canvas');
      salida.width = Math.round(cw * escala);
      salida.height = Math.round(ch * escala);
      const sctx = salida.getContext('2d');
      if (!sctx) return canvas.toDataURL('image/png');
      sctx.imageSmoothingEnabled = true;
      sctx.imageSmoothingQuality = 'high';
      sctx.drawImage(canvas, x0, y0, cw, ch, 0, 0, salida.width, salida.height);
      return salida.toDataURL('image/png');
    } catch {
      return canvas.toDataURL('image/png');
    }
  };

  const handleEnd = () => {
    if (!sigRef.current) return;
    const empty = sigRef.current.isEmpty();
    setIsEmpty(empty);
    if (onChange) {
      onChange(empty ? null : exportarFirma());
    }
  };

  const handleClear = () => {
    sigRef.current?.clear();
    setIsEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="w-full">
      {/* Label superior */}
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">
          Firma del profesional
          <span className="text-red-500 ml-1">*</span>
        </label>
        {signerLabel && (
          <span className="text-xs text-gray-500">{signerLabel}</span>
        )}
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className={`relative border-2 rounded-xl overflow-hidden bg-white transition-colors ${
          errorMessage
            ? 'border-red-300'
            : isEmpty
            ? 'border-gray-300'
            : 'border-purple-400'
        }`}
        style={{ height: 200 }}
      >
        <SignatureCanvas
          ref={sigRef}
          penColor="#1F2937"
          minWidth={1}
          maxWidth={2.5}
          velocityFilterWeight={0.7}
          canvasProps={{
            width: canvasWidth,
            height: 200,
            className: 'block',
            style: { touchAction: 'none', cursor: disabled ? 'not-allowed' : CURSOR_CRUZ },
          }}
          onEnd={handleEnd}
        />

        {/* Placeholder cuando está vacío */}
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-gray-300 text-sm italic select-none">
              Firmá acá con el mouse, dedo o stylus
            </span>
          </div>
        )}

        {/* Línea base de firma (decorativa) */}
        <div className="absolute left-4 right-4 bottom-8 border-b border-dashed border-gray-200 pointer-events-none" />
      </div>

      {/* Acciones + estado */}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2 text-xs">
          {errorMessage ? (
            <span className="flex items-center gap-1 text-red-600">
              <AlertCircle className="w-4 h-4" />
              {errorMessage}
            </span>
          ) : !isEmpty ? (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle className="w-4 h-4" />
              Firma capturada
            </span>
          ) : (
            <span className="text-gray-400">Sin firma todavía</span>
          )}
        </div>

        <button
          type="button"
          onClick={handleClear}
          disabled={isEmpty || disabled}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Eraser className="w-3.5 h-3.5" />
          Borrar y reintentar
        </button>
      </div>

      {/* Aviso legal */}
      <p className="mt-3 text-xs text-gray-500 leading-relaxed">
        Al firmar manifestás tu consentimiento sobre el contenido de la receta.
        La imagen se almacena <span className="font-medium">cifrada</span> con
        AES-256-GCM en nuestros servidores y solo se desencripta para mostrarla en el
        PDF de la receta.
      </p>
    </div>
  );
};

export default SignaturePad;
